/**
 * Artifacts Module Constants
 *
 * Constantes centralizadas para o módulo de artefatos
 */

// =============================================================================
// ARTIFACT TYPES
// =============================================================================

/**
 * Tipos MIME suportados para artefatos
 */
export const ARTIFACT_TYPES = {
  // Texto
  MARKDOWN: 'text/markdown',
  HTML: 'text/html',
  PLAIN: 'text/plain',
  CSV: 'text/csv',

  // Código
  CODE: 'application/vnd.ant.code',

  // Documentos
  PDF: 'application/pdf',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  DOCX_STRUCTURED: 'application/vnd.tais.docx-structured',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  // Tipos customizados TAIS (tributário)
  TAX_COMPARISON: 'application/vnd.tais.tax-comparison',
  TAX_CHART: 'application/vnd.tais.tax-chart',
  TAX_BAR_CHART: 'application/vnd.tais.tax-bar-chart',
  TAX_SUMMARY: 'application/vnd.tais.tax-summary',
} as const;

export type ArtifactType = (typeof ARTIFACT_TYPES)[keyof typeof ARTIFACT_TYPES];

// =============================================================================
// DOWNLOAD FORMATS
// =============================================================================

/**
 * Formatos de download disponíveis
 */
export const DOWNLOAD_FORMATS = {
  TXT: 'txt',
  MD: 'md',
  HTML: 'html',
  PDF: 'pdf',
  DOCX: 'docx',
  CSV: 'csv',
  XLSX: 'xlsx',
} as const;

export type DownloadFormat = (typeof DOWNLOAD_FORMATS)[keyof typeof DOWNLOAD_FORMATS];

/**
 * Mapeamento de tipo de artefato para formato nativo de download
 * Cada tipo tem UM formato nativo - sem dropdown, download direto
 */
export const NATIVE_FORMAT_MAP: Record<string, DownloadFormat> = {
  // Texto
  [ARTIFACT_TYPES.MARKDOWN]: DOWNLOAD_FORMATS.MD,
  [ARTIFACT_TYPES.HTML]: DOWNLOAD_FORMATS.HTML,
  [ARTIFACT_TYPES.PLAIN]: DOWNLOAD_FORMATS.TXT,
  [ARTIFACT_TYPES.CSV]: DOWNLOAD_FORMATS.CSV,

  // Código - baixa como TXT
  [ARTIFACT_TYPES.CODE]: DOWNLOAD_FORMATS.TXT,

  // Documentos
  [ARTIFACT_TYPES.PDF]: DOWNLOAD_FORMATS.PDF,
  [ARTIFACT_TYPES.DOCX]: DOWNLOAD_FORMATS.DOCX,
  [ARTIFACT_TYPES.DOCX_STRUCTURED]: DOWNLOAD_FORMATS.DOCX,
  [ARTIFACT_TYPES.XLSX]: DOWNLOAD_FORMATS.XLSX,

  // Tax components - exporta como PDF (gráficos/tabelas)
  [ARTIFACT_TYPES.TAX_COMPARISON]: DOWNLOAD_FORMATS.PDF,
  [ARTIFACT_TYPES.TAX_CHART]: DOWNLOAD_FORMATS.PDF,
  [ARTIFACT_TYPES.TAX_BAR_CHART]: DOWNLOAD_FORMATS.PDF,
  [ARTIFACT_TYPES.TAX_SUMMARY]: DOWNLOAD_FORMATS.PDF,
};

/**
 * MIME types para cada formato de download
 */
export const MIME_TYPES: Record<DownloadFormat, string> = {
  [DOWNLOAD_FORMATS.TXT]: 'text/plain;charset=utf-8',
  [DOWNLOAD_FORMATS.MD]: 'text/markdown;charset=utf-8',
  [DOWNLOAD_FORMATS.HTML]: 'text/html;charset=utf-8',
  [DOWNLOAD_FORMATS.PDF]: 'application/pdf',
  [DOWNLOAD_FORMATS.DOCX]: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [DOWNLOAD_FORMATS.CSV]: 'text/csv;charset=utf-8',
  [DOWNLOAD_FORMATS.XLSX]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

// =============================================================================
// UI CONSTANTS
// =============================================================================

/**
 * Labels para badges de tipo
 */
export const TYPE_LABELS: Record<string, string> = {
  [ARTIFACT_TYPES.MARKDOWN]: 'Markdown',
  [ARTIFACT_TYPES.HTML]: 'HTML',
  [ARTIFACT_TYPES.PLAIN]: 'Texto',
  [ARTIFACT_TYPES.CSV]: 'CSV',
  [ARTIFACT_TYPES.CODE]: 'Código',
  [ARTIFACT_TYPES.PDF]: 'PDF',
  [ARTIFACT_TYPES.DOCX]: 'Word',
  [ARTIFACT_TYPES.DOCX_STRUCTURED]: 'Word',
  [ARTIFACT_TYPES.XLSX]: 'Excel',
  [ARTIFACT_TYPES.TAX_COMPARISON]: 'Comparativo',
  [ARTIFACT_TYPES.TAX_CHART]: 'Gráfico',
  [ARTIFACT_TYPES.TAX_BAR_CHART]: 'Gráfico',
  [ARTIFACT_TYPES.TAX_SUMMARY]: 'Resumo',
};

/**
 * Cores para badges de tipo
 */
export const TYPE_COLORS: Record<string, { bg: string; text: string; icon: string }> = {
  [ARTIFACT_TYPES.MARKDOWN]: { bg: '#1a1a2e', text: '#60a5fa', icon: '#3b82f6' },
  [ARTIFACT_TYPES.HTML]: { bg: '#2d1f1f', text: '#f97316', icon: '#ea580c' },
  [ARTIFACT_TYPES.PLAIN]: { bg: '#1f1f1f', text: '#9ca3af', icon: '#6b7280' },
  [ARTIFACT_TYPES.CSV]: { bg: '#1f2d1f', text: '#4ade80', icon: '#22c55e' },
  [ARTIFACT_TYPES.CODE]: { bg: '#1f1a2e', text: '#a78bfa', icon: '#8b5cf6' },
  [ARTIFACT_TYPES.PDF]: { bg: '#2d1f1f', text: '#f87171', icon: '#ef4444' },
  [ARTIFACT_TYPES.DOCX]: { bg: '#1f1f2d', text: '#60a5fa', icon: '#2563eb' },
  [ARTIFACT_TYPES.DOCX_STRUCTURED]: { bg: '#1f1f2d', text: '#60a5fa', icon: '#2563eb' },
  [ARTIFACT_TYPES.XLSX]: { bg: '#1f2d1f', text: '#4ade80', icon: '#16a34a' },
  [ARTIFACT_TYPES.TAX_COMPARISON]: { bg: '#2d2d1f', text: '#fbbf24', icon: '#f59e0b' },
  [ARTIFACT_TYPES.TAX_CHART]: { bg: '#2d2d1f', text: '#fbbf24', icon: '#f59e0b' },
  [ARTIFACT_TYPES.TAX_BAR_CHART]: { bg: '#2d2d1f', text: '#fbbf24', icon: '#f59e0b' },
  [ARTIFACT_TYPES.TAX_SUMMARY]: { bg: '#2d2d1f', text: '#fbbf24', icon: '#f59e0b' },
};

// =============================================================================
// PANEL CONSTANTS
// =============================================================================

/**
 * Configurações do painel de artefatos
 */
export const PANEL_CONFIG = {
  MIN_WIDTH: 400,
  MAX_WIDTH_RATIO: 0.85,
  STORAGE_KEY: 'tais-artifact-panel-width',

  // Breakpoints responsivos
  BREAKPOINTS: {
    XL: { min: 1400, defaultRatio: 0.4 },
    LG: { min: 1024, defaultRatio: 0.45 },
    MD: { min: 769, defaultRatio: 0.5 },
    SM: { min: 0, defaultRatio: 1.0 },
  },
} as const;

/**
 * Calcula largura inicial do painel baseado no tamanho da tela
 */
export const getInitialPanelWidth = (): number => {
  const saved = localStorage.getItem(PANEL_CONFIG.STORAGE_KEY);

  if (saved) {
    const width = parseInt(saved, 10);
    if (!isNaN(width)) {
      return Math.max(
        PANEL_CONFIG.MIN_WIDTH,
        Math.min(window.innerWidth * PANEL_CONFIG.MAX_WIDTH_RATIO, width)
      );
    }
  }

  const { BREAKPOINTS } = PANEL_CONFIG;
  const screenWidth = window.innerWidth;

  if (screenWidth >= BREAKPOINTS.XL.min) return screenWidth * BREAKPOINTS.XL.defaultRatio;
  if (screenWidth >= BREAKPOINTS.LG.min) return screenWidth * BREAKPOINTS.LG.defaultRatio;
  if (screenWidth >= BREAKPOINTS.MD.min) return screenWidth * BREAKPOINTS.MD.defaultRatio;
  return screenWidth;
};

/**
 * Salva largura do painel no localStorage
 */
export const savePanelWidth = (width: number): void => {
  const validWidth = Math.max(
    PANEL_CONFIG.MIN_WIDTH,
    Math.min(window.innerWidth * PANEL_CONFIG.MAX_WIDTH_RATIO, width)
  );
  localStorage.setItem(PANEL_CONFIG.STORAGE_KEY, validWidth.toString());
};

// =============================================================================
// ANIMATION CONSTANTS
// =============================================================================

export const ANIMATION_DURATIONS = {
  SLIDE_IN: 300,
  UPDATE_FLASH: 300,
  PROGRESS_COMPLETE: 2000,
} as const;

// =============================================================================
// ERROR MESSAGES
// =============================================================================

export const ERROR_MESSAGES = {
  DOWNLOAD_FAILED: 'Erro ao fazer download do arquivo',
  RENDER_FAILED: 'Erro ao renderizar artefato',
  PARSE_FAILED: 'Erro ao processar conteúdo do artefato',
  UNSUPPORTED_TYPE: 'Tipo de artefato não suportado',
  COPY_FAILED: 'Erro ao copiar conteúdo',
} as const;

// =============================================================================
// SUCCESS MESSAGES
// =============================================================================

export const SUCCESS_MESSAGES = {
  DOWNLOAD_COMPLETE: 'Download concluído!',
  COPY_SUCCESS: 'Conteúdo copiado!',
} as const;
